/* Collections gallery (sections/collections-gallery.liquid).
   Phones and tablets: the row scrolls natively with snap points; the arrows move it one view at a time and the
   gold progress line follows the scroll.
   Desktop: one collection at a time. The gold line under the small photograph on show fills (a CSS animation as
   long as the section's dwell); when it is full the next collection comes in: its photograph fades in over the
   last, the words go and the new words follow. The turning holds while a pointer rests on the spread or the
   strip, while a key is in it, while it is out of view or the tab is hidden, and when the round button is
   pressed; for visitors who ask for reduced motion it does not start (the button starts it).
   Each wordmark is measured once the page is idle: the extent of its ink becomes the box's aspect ratio and the
   file is shown as a mask of that part, so marks drawn with very different margins stand at one height. */
(() => {
  if (customElements.get('vr-gallery')) return;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class VrGallery extends HTMLElement {
    connectedCallback() {
      this.setupSpread();
      this.track = this.querySelector('.vr-gal__track');
      this.nav = this.querySelector('.vr-gal__nav');
      this.prev = this.querySelector('[data-vr-gal="prev"]');
      this.next = this.querySelector('[data-vr-gal="next"]');
      this.bar = this.querySelector('.vr-gal__bar');
      if (!this.track) return;
      this.onScroll = () => { if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); }); };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.prev && this.prev.addEventListener('click', () => this.page(-1));
      this.next && this.next.addEventListener('click', () => this.page(1));
      this.resizer = new ResizeObserver(() => this.update());
      this.resizer.observe(this.track);
      this.update();
      this.measureMarks();
    }

    disconnectedCallback() {
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      if (this.resizer) this.resizer.disconnect();
      if (this.spreadSeen) this.spreadSeen.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    /* ---- desktop: one collection at a time ---- */
    setupSpread() {
      const spread = this.querySelector('[data-vr-spread]');
      if (!spread || this.spread) return;
      this.spread = spread;
      this.photos = Array.from(spread.querySelectorAll('.vr-spread__img'));
      this.texts = Array.from(spread.querySelectorAll('.vr-spread__text'));
      this.thumbs = Array.from(spread.querySelectorAll('.vr-spread__thumb'));
      this.photoLink = spread.querySelector('.vr-spread__photo');
      this.panel = spread.querySelector('.vr-spread__texts');
      this.pauseButton = spread.querySelector('.vr-spread__pause');
      this.at = 0;
      this.held = { pointer: false, keys: false, button: reducedMotion.matches, hidden: true };
      const count = this.thumbs.length;
      if (count < 2) return;

      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this.show(i));
        thumb.addEventListener('keydown', (event) => {
          let to = -1;
          if (event.key === 'ArrowRight') to = (i + 1) % count;
          else if (event.key === 'ArrowLeft') to = (i - 1 + count) % count;
          else if (event.key === 'Home') to = 0;
          else if (event.key === 'End') to = count - 1;
          if (to < 0) return;
          event.preventDefault();
          this.show(to);
          this.thumbs[to].focus();
        });
      });
      /* the line under the one on show is full: the next one */
      spread.addEventListener('animationend', (event) => {
        if (!event.target.classList.contains('vr-spread__fill')) return;
        if (!event.target.closest('.vr-spread__thumb.is-active')) return;
        this.show((this.at + 1) % count);
      });
      spread.addEventListener('mouseenter', () => this.hold('pointer', true));
      spread.addEventListener('mouseleave', () => this.hold('pointer', false));
      spread.addEventListener('focusin', (event) => this.hold('keys', event.target.matches(':focus-visible')));
      spread.addEventListener('focusout', (event) => { if (!spread.contains(event.relatedTarget)) this.hold('keys', false); });
      if (this.pauseButton) {
        this.pauseButton.hidden = false;
        this.pauseButton.addEventListener('click', () => {
          spread.classList.remove('is-still');
          this.hold('button', !this.held.button);
        });
      }
      this.spreadSeen = new IntersectionObserver((entries) => {
        entries.forEach((entry) => this.hold('hidden', !entry.isIntersecting));
      }, { threshold: 0.35 });
      this.spreadSeen.observe(spread);
      this.onVisibility = () => this.hold('hidden', document.visibilityState === 'hidden' || !this.inView());
      document.addEventListener('visibilitychange', this.onVisibility);
      /* with reduced motion the line under the one on show stays full and nothing turns until the button is pressed */
      if (!reducedMotion.matches) spread.classList.remove('is-still');
      this.hold('button', this.held.button);
    }

    inView() {
      const r = this.spread.getBoundingClientRect();
      return r.bottom > 0 && r.top < window.innerHeight;
    }

    hold(reason, on) {
      this.held[reason] = on;
      const paused = this.held.pointer || this.held.keys || this.held.button || this.held.hidden;
      this.spread.classList.toggle('is-paused', paused);
      if (this.pauseButton) {
        this.pauseButton.setAttribute('aria-pressed', this.held.button ? 'true' : 'false');
        this.pauseButton.setAttribute('aria-label', this.held.button ? 'Play the turning of the collections' : 'Pause the turning of the collections');
      }
    }

    show(to) {
      const from = this.at;
      if (to === from || !this.thumbs[to]) return;
      this.at = to;
      const next = this.photos[to];
      const swap = () => {
        if (this.at !== to) return;
        const prev = this.photos[from];
        /* the new photograph fades in over the old one, which stays whole beneath it until it is covered */
        this.photos.forEach((photo) => { if (photo !== prev && photo !== next) photo.classList.remove('is-active', 'is-leaving'); });
        if (prev && prev !== next) {
          prev.classList.add('is-leaving');
          prev.classList.remove('is-active');
          clearTimeout(this.leaveTimer);
          this.leaveTimer = setTimeout(() => prev.classList.remove('is-leaving'), 1300);
        }
        next.classList.add('is-active');
      };
      if (next && next.tagName === 'IMG' && !(next.complete && next.naturalWidth)) {
        next.loading = 'eager';
        (next.decode ? next.decode() : Promise.resolve()).then(swap, swap);
      } else {
        swap();
      }
      this.texts.forEach((text, i) => {
        const on = i === to;
        text.classList.toggle('is-active', on);
        if (on) { text.removeAttribute('aria-hidden'); text.inert = false; } else { text.setAttribute('aria-hidden', 'true'); text.inert = true; }
      });
      this.thumbs.forEach((thumb, i) => {
        const on = i === to;
        thumb.classList.toggle('is-active', on);
        thumb.setAttribute('aria-selected', on ? 'true' : 'false');
        thumb.tabIndex = on ? 0 : -1;
      });
      if (this.panel) this.panel.setAttribute('aria-labelledby', this.thumbs[to].id);
      if (this.photoLink && this.texts[to]) this.photoLink.href = this.texts[to].dataset.url;
    }

    // one view at a time, landing on a card's edge (the snap points do the rest)
    page(dir) {
      const t = this.track;
      t.scrollBy({ left: dir * t.clientWidth, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    update() {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      const scrollable = max > 4;
      if (this.nav) this.nav.hidden = !scrollable;
      if (!scrollable) return;
      const share = t.clientWidth / t.scrollWidth;
      const at = Math.min(1, Math.max(0, t.scrollLeft / max));
      if (this.bar) {
        this.bar.style.setProperty('--vr-gal-w', (share * 100).toFixed(2) + '%');
        this.bar.style.setProperty('--vr-gal-x', ((1 - share) / share * at * 100).toFixed(2) + '%');
      }
      if (this.prev) this.prev.disabled = t.scrollLeft <= 2;
      if (this.next) this.next.disabled = t.scrollLeft >= max - 2;
    }

    measureMarks() {
      const boxes = Array.from(this.querySelectorAll('.vr-index__mark-box[data-vr-mark]'));
      if (!boxes.length) return;
      const byUrl = new Map();
      boxes.forEach((b) => { const u = b.dataset.vrMark; if (!byUrl.has(u)) byUrl.set(u, []); byUrl.get(u).push(b); });
      const run = () => byUrl.forEach((list, url) => {
        const im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = () => {
          try {
            const W = 240, H = Math.max(1, Math.round(W * im.naturalHeight / im.naturalWidth));
            const c = document.createElement('canvas'); c.width = W; c.height = H;
            const g = c.getContext('2d'); g.drawImage(im, 0, 0, W, H);
            const d = g.getImageData(0, 0, W, H).data;
            let x0 = W, y0 = H, x1 = -1, y1 = -1;
            for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) { if (d[(y * W + x) * 4 + 3] > 40) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; } }
            if (x1 < 0) return;
            const fw = (x1 + 1 - x0) / W, fh = (y1 + 1 - y0) / H, fx = x0 / W, fy = y0 / H;
            const ar = (fw * im.naturalWidth) / (fh * im.naturalHeight);
            const px = fw >= 0.999 ? 50 : fx / (1 - fw) * 100, py = fh >= 0.999 ? 50 : fy / (1 - fh) * 100;
            list.forEach((b) => {
              b.style.setProperty('--ink-ar', ar.toFixed(4));
              b.style.setProperty('--ink-boost', Math.min(1.42, Math.max(0.78, Math.sqrt(1.7 / ar))).toFixed(3));
              b.style.setProperty('--ink-size', (100 / fw).toFixed(2) + '% ' + (100 / fh).toFixed(2) + '%');
              b.style.setProperty('--ink-pos', px.toFixed(2) + '% ' + py.toFixed(2) + '%');
              b.classList.add('is-measured');
            });
          } catch (e) { /* a file the canvas may not read: shown whole, as drawn */ }
        };
        im.src = url;
      });
      if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 2000 }); else setTimeout(run, 500);
    }
  }

  customElements.define('vr-gallery', VrGallery);
})();
